import { type ReactNode, useLayoutEffect, useRef } from "react"
import { HEIGHT_DURATION, HEIGHT_EASE } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"

/**
 * Блок, высота которого плавно идёт за содержимым: в таблицу добавили строку, из списка
 * удалили элемент, раскрылась подсказка. То, что ниже, отъезжает, а не прыгает. Строки
 * таблицы по высоте не анимируются, поэтому высоту ведёт обёртка.
 *
 * Замер идёт в ResizeObserver, то есть после раскладки и до отрисовки: прежняя высота
 * возвращается в том же кадре, и рывка не видно. Края обрезаются только на время перехода.
 * Не для полей ввода, которые растут при наборе: там высота должна успевать за текстом.
 */
export function AutoHeight({ children, className }: { children: ReactNode; className?: string }) {
	const outer = useRef<HTMLDivElement>(null)
	const inner = useRef<HTMLDivElement>(null)

	useLayoutEffect(() => {
		const box = outer.current
		const content = inner.current
		if (!box || !content) return
		let settled = content.offsetHeight
		let animating = false
		let timer = 0
		const finish = () => {
			animating = false
			box.style.transition = ""
			box.style.height = ""
			box.style.overflow = ""
		}
		const observer = new ResizeObserver(() => {
			const next = content.offsetHeight
			if (Math.abs(next - settled) < 1) return
			const from = animating ? box.getBoundingClientRect().height : settled
			settled = next
			animating = true
			box.style.transition = "none"
			box.style.height = `${from}px`
			box.style.overflow = "clip"
			void box.offsetHeight
			box.style.transition = `height ${HEIGHT_DURATION}ms ${HEIGHT_EASE}`
			box.style.height = `${next}px`
			window.clearTimeout(timer)
			timer = window.setTimeout(finish, HEIGHT_DURATION + 40)
		})
		observer.observe(content)
		return () => {
			observer.disconnect()
			window.clearTimeout(timer)
		}
	}, [])

	return (
		<div ref={outer} className={className}>
			<div ref={inner} className={cn("flow-root")}>
				{children}
			</div>
		</div>
	)
}
